import { useEffect, useRef, useCallback } from "react";
import { API_URL, fetchWithAuth } from "../Componentes/fetchWithAuth";

/**
 * Hook para registrar la actividad del usuario en toda la aplicación.
 * @param {Object} config - Configuración opcional
 * @param {boolean} config.enable - Habilita o deshabilita el logging
 * @param {boolean} config.debug - Muestra logs en la consola del navegador
 */
export const useActivityLogger = ({ enable = true, debug = false } = {}) => {
  const logs = useRef([]);

  const addLog = useCallback(
    (eventData) => {
      const timestamp = new Date().toISOString();
      const newEntry = { ...eventData, serverTime: timestamp };

      logs.current.push(newEntry);

      if (debug) {
        console.log("📝 [Activity Log]:", newEntry);
      }
    },
    [debug],
  );

  // Helper para encontrar el identificador más cercano
  const getElementIdentifier = (element) => {
    let current = element;

    // Configuración para detectar elementos específicos de PrimeReact y otros casos conocidos
    const specialElements = [
      {
        check: (el) =>
          el.classList &&
          (el.classList.contains("p-row-toggler") ||
            el.classList.contains("p-row-toggler-icon")),
        param: "RowToggler",
      },
      {
        check: (el) => el.classList && el.classList.contains("p-column-title"),
        param: "ColumnHeader",
      },
    ];

    // Buscamos hacia arriba hasta encontrar un ID, Name, o llegar al body
    while (current && current !== document.body) {
      const el = current; // Captura local para evitar no-loop-func
      // 1. Chequeo de elementos especiales (ej. RowToggler)
      const special = specialElements.find((s) => s.check(el));
      if (special) {
        // Intentar buscar una clave de fila cercana si es un toggler
        // PrimeReact suele poner atributos en el TR o dentro del componente, pero es difícil acceder desde aquí genéricamente.
        // Si el innerText es numérico (a veces pasa), lo usamos.
        return { id: special.param, tag: "BUTTON" };
      }

      // Helper para validar si un ID es útil (no es "undefined ...")
      const isValidId = (val) =>
        val &&
        typeof val === "string" &&
        !val.trim().startsWith("undefined") &&
        !val.trim().startsWith("null");

      if (current.id && isValidId(current.id))
        return { id: current.id, tag: current.tagName };
      if (current.name && isValidId(current.name))
        return { id: current.name, tag: current.tagName };

      const ariaLabel = current.getAttribute
        ? current.getAttribute("aria-label")
        : null;
      if (ariaLabel && isValidId(ariaLabel)) {
        return { id: ariaLabel, tag: current.tagName };
      }

      // Si encontramos un botón o link sin ID, devolvemos eso al menos
      if (
        ["BUTTON", "A", "INPUT", "SELECT", "TEXTAREA"].includes(current.tagName)
      ) {
        let textId =
          current.innerText?.substring(0, 30).trim() || "unnamed-control";
        // Si el texto empieza con undefined, lo limpiamos
        if (textId.startsWith("undefined"))
          textId = "unnamed-control-undefined";

        return {
          id: textId,
          tag: current.tagName,
        };
      }

      // Si el elemento clickeado es un SVG o PATH (íconos), seguimos subiendo pero recordamos que fue un ícono
      current = current.parentElement;
    }
    return { id: "unknown-element", tag: element.tagName };
  };

  // Manejador de Clicks/Toques (Usamos pointerdown para soportar móvil y desktop)
  const handlePointerDown = useCallback(
    (e) => {
      if (!enable) return;

      // Importante: No bloqueamos la propagación para no romper la app

      let clickType = "normal";
      if (e.button === 2) clickType = "right-click";
      else if (e.button === 0) clickType = "left-click";

      const element = e.target;
      const { id, tag } = getElementIdentifier(element);

      addLog({
        eventType: "CLICK",
        clickType: clickType,
        elementTarget: id,
        elementTag: tag,
        originalTag: element.tagName,
        className: element.className,
        x: Math.round(e.clientX),
        y: Math.round(e.clientY),
      });
    },
    [enable, addLog],
  );

  // Manejador de Teclado
  const handleKeyDown = useCallback(
    (e) => {
      if (!enable) return;

      const element = e.target;

      // Seguridad: Ignorar campos de contraseña para no registrar teclas
      if (element.type === "password") return;

      const tagName = element.tagName
        ? element.tagName.toUpperCase()
        : "UNKNOWN";
      const isInput = ["INPUT", "TEXTAREA"].includes(tagName);

      if (debug) {
        // console.log(`⌨️ [KeyDown Debug] Key: ${e.key}, Tag: ${tagName}, IsInput: ${isInput}`);
      }

      if (e.key === "Tab") {
        addLog({
          eventType: "TAB_PRESS",
          elementTarget: element.id || element.name || "unknown",
          elementTag: tagName,
        });
        return;
      }

      if (isInput) {
        addLog({
          eventType: "KEY_INPUT",
          key: e.key,
          elementTarget: element.id || element.name || "unknown",
          elementTag: tagName,
        });
      }
    },
    [enable, addLog, debug],
  );

  // Función para guardar los logs en el backend
  const saveLogs = useCallback(async () => {
    if (logs.current.length === 0) return;

    const logsToSend = [...logs.current];
    try {
      // Usamos fetch para enviar los datos al backend
      const response = await fetchWithAuth(`${API_URL}/api/user-activity`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(logsToSend),
      });

      if (response.ok) {
        if (debug)
          console.log(
            `✅ [Activity Log] ${logsToSend.length} logs guardados en el servidor.`,
          );
        logs.current = [];
      } else {
        if (debug)
          console.error(
            "❌ [Activity Log] Error guardando logs:",
            response.statusText,
          );
      }
    } catch (error) {
      if (debug) console.error("❌ [Activity Log] Error de red:", error);
    }
  }, [debug]);

  useEffect(() => {
    if (!enable) return;

    // Usamos 'pointerdown' para capturar clicks en Web y Móvil consistentemente
    document.addEventListener("pointerdown", handlePointerDown, {
      capture: true,
    });
    document.addEventListener("keydown", handleKeyDown, { capture: true });

    // Guardado automático periódico
    const intervalId = setInterval(saveLogs, 30000); // 30 segundos

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, {
        capture: true,
      });
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
      clearInterval(intervalId);
    };
  }, [handlePointerDown, handleKeyDown, saveLogs, enable]);

  const getLogs = () => logs.current;
  const clearLogs = () => {
    logs.current = [];
  };

  return { getLogs, clearLogs, saveLogs };
};
